import { Button, Typography } from '@mui/material';
import {
  formatExercisePayload,
  getExerciseDisplay,
  getExercisesFromEntryWithSection,
  getShorthandMetrics,
  shouldSkipEntry,
  WorkoutAlternateGroup,
  WorkoutExercise,
  WorkoutSection,
  WorkoutSuperset,
} from '@strength/domain-workout';
import { MdAdd, MdChevronLeft } from '@teamworksdev/icons';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { WorkoutStatusBadge } from '../../../../components/badge/workout-status-badge';
import { cn } from '../../../../components/utils';
import { useMetrics } from '../../../../hooks/useMetrics';
import { getEntryKey } from '../../utils';
import { useTabletContext } from '../context/TabletContext';
import { useWorkoutContext } from '../context/WorkoutContext';

export const WorkoutOverview = () => {
  const { t } = useTranslation();
  const { onSelectWorkout, onSelectSection, athleteSession } =
    useTabletContext();

  const {
    selectedAthlete,
    workout,
    exerciseLabelMap,
    handleOpenAddExerciseDrawer,
    handleOpenCompleteWorkoutDialog,
  } = useWorkoutContext();

  const sections = workout?.sections ?? [];

  if (!workout) return null;

  return (
    <>
      <div className="flex flex-col grow overflow-hidden">
        <div className="h-[44px] flex bg-gray-50 border-b border-gray-200 justify-between items-center w-full px-1 py-1">
          <Button
            variant="text"
            className="p-2 flex flex-row items-center gap-2"
            onClick={() => {
              onSelectWorkout(undefined, undefined, selectedAthlete.id);
            }}
          >
            <MdChevronLeft size={16} />
            {athleteSession[selectedAthlete.id]?.workoutName}
          </Button>
          <Button
            variant="text"
            className="p-2"
            onClick={handleOpenCompleteWorkoutDialog}
          >
            {t('completeWorkout')}
          </Button>
        </div>

        <div className="flex flex-col grow overflow-hidden">
          <div className="flex flex-col overflow-y-auto">
            {sections.map((section) => (
                <Button
                  variant="outlined"
                  key={section.workoutSectionId}
                  className="border-t-0 border-x-0 rounded-none shadow-none flex-col items-start w-full flex-shrink-0"
                  onClick={() => {
                    onSelectSection(
                      section.workoutSectionId,
                      selectedAthlete.id,
                    );
                  }}
                >
                  <div className="py-3 flex items-start px-4 flex-col gap-1">
                    <Typography variant="h4">{section.name}</Typography>
                    <Typography
                      variant="caption"
                      style={{ whiteSpace: 'pre-wrap' }}
                    >
                      {section.instruction}
                    </Typography>
                  </div>
                  <div className="flex flex-col gap-3 pb-4">
                    {section.entries.map((entry) => {
                      // Skip WorkoutAlternateGroups that overlap with WorkoutSupersets
                      if (shouldSkipEntry(entry, section.entries)) {
                        return null;
                      }

                      return (
                        <SectionExerciseDescription
                          key={`${section.workoutSectionId}-${getEntryKey(
                            entry,
                          )}-${entry.sortOrder}`}
                          entry={entry}
                          exerciseLabelMap={exerciseLabelMap}
                          section={section}
                        />
                      );
                    })}
                  </div>
                </Button>
              ))}
          </div>
        </div>
      </div>
      <Button
        className="absolute bottom-4 right-4 self-end flex flex-row items-center gap-2 bg-white"
        variant="outlined"
        onClick={() => handleOpenAddExerciseDrawer()}
      >
        <MdAdd size={24} />
        {t('addExercise')}
      </Button>
    </>
  );
};

type SectionExerciseDescriptionProps = {
  entry: WorkoutExercise | WorkoutSuperset | WorkoutAlternateGroup;
  exerciseLabelMap: Record<string, string>;
  section: WorkoutSection;
};

const SectionExerciseDescription = ({
  entry,
  exerciseLabelMap,
  section,
}: SectionExerciseDescriptionProps) => {
  const { t } = useTranslation();
  const { getMetrics } = useMetrics();
  const { actualLayer, applyBenchmarkData, blockType } = useWorkoutContext();
  const isSuperset = entry.__type === 'WorkoutSuperset';

  const metrics = useMemo(() => {
    return getMetrics(true);
  }, [getMetrics]);

  const exercises = useMemo(() => {
    return getExercisesFromEntryWithSection(entry, section, actualLayer);
  }, [entry, section, actualLayer]);

  if (exercises.length === 0) return null;

  return (
    <div className="flex flex-col">
      {exercises.map((exercise, index) => {
        const exerciseGroup =
          exerciseLabelMap[exercise.workoutExerciseId] ?? '';

        const condensedMetrics = getShorthandMetrics(
          formatExercisePayload(
            exercise,
            exerciseGroup,
            entry.__type === 'WorkoutAlternateGroup'
              ? entry.workoutAlternateGroupId
              : undefined,
          ),
          exercise.libraryExerciseId,
          applyBenchmarkData,
          exercise.metrics,
          exercise.sets,
          metrics,
          t,
          blockType,
        );

        return (
          <div
            key={exercise.workoutExerciseId}
            className={cn(
              'flex flex-col ml-3 gap-2',
              isSuperset && 'border-l-4 border-informational-700',
              index !== exercises.length - 1 && 'pb-3',
            )}
          >
            <div className="flex flex-col pl-4 items-start">
              <div className="flex flex-row gap-2 items-center">
                {isSuperset && (
                  <Typography variant="body1" className="font-semibold">
                    {exerciseGroup}
                  </Typography>
                )}
                <Typography variant="body1">
                  {getExerciseDisplay(
                    {
                      exerciseName: exercise.exerciseName,
                      isEachSide: exercise.isEachSide,
                    },
                    t,
                  )}
                </Typography>
                {exercise.status && exercise.status !== 'planned' && (
                  <WorkoutStatusBadge status={{ type: exercise.status }} />
                )}
              </div>
              {exercise.instruction && (
                <Typography
                  variant="subtitle1"
                  className="text-black/60 whitespace-pre-line text-left"
                >
                  {exercise.instruction}
                </Typography>
              )}
            </div>
            <div className="flex flex-col gap-1 pl-4 items-start">
              {condensedMetrics.map((metric, index) => (
                <Typography
                  key={`${exerciseGroup}-${exercise.exerciseName}-${index}`}
                  variant="subtitle1"
                  className="text-black/60"
                >
                  {metric}
                </Typography>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
